import React from "react";
import { useSelector } from "react-redux";
import { newColumn, newColumnConfig, newColumnPercentage, newColumnText, newColumnValueWithNumberComponent } from "../../../TableList";
import { FormattedMessage, injectIntl } from "react-intl";
import { Grid, Typography } from "@material-ui/core";
import useUserByGroupReportData from "../../../../../hooks/useUserByGroupReportData";
import PageContainer from "../../../../../custom/Page";
import UserFilters from "../UsersFilters";
import AnimatedCardContent from "../../../AnimatedCardContent";
import ReportTable from "../../ReportTable";
import { ReportByGroupParam, formatCurrency, formatPercentage } from "../../../../../util";
import HeaderReportUsersGroup from "./HeaderReportUsersGroup";
import UsersReportSummary from "../UsersReportSummary";

const renderGroup = (value, groupType, intl) => {
  if (!value) {
    return intl.formatMessage({ id: "app.notAvailable" });
  }

  if (groupType === ReportByGroupParam.GENDER) {
    return intl.formatMessage({ id: `app.enuns.${value}` });
  }

  return value;
};

const ReportByGroupContext = ({ pageTitle, tableTitle, userFilters, setUserFilters, reportType, fetchCitiesEnabled, fetchStatesEnabled, WrapperComponent = Wrapper, intl }) => {
  const currentUser = useSelector((state) => state.user.user.me);

  const { reportData, loading, error } = useUserByGroupReportData(userFilters, reportType);

  const availableColumns = React.useMemo(() => {
    return [
      {
        name: "group",
        label: "app.group",
        enable: true,
        definition: () => newColumnText("app.group", "group", (rowData) => renderGroup(rowData.group, userFilters.group, intl)),
        exportValue: (rowData) => renderGroup(rowData.group, userFilters.group, intl),
      },
      {
        name: "userCount",
        label: "app.user.total",
        enable: true,
        definition: () => newColumnConfig({ title: "app.user.total", field: "userCount", align: "center", render: (rowData) => rowData.userCount ?? 0 }),
        exportValue: (rowData) => rowData.userCount,
      },
      {
        name: "userCountPercent",
        label: "app.users.percent",
        enable: true,
        definition: () => newColumnPercentage("app.users.percent", "userCountPercent", true),
        exportValue: (rowData) => formatPercentage({ value: rowData.userCountPercent, userLocale: currentUser.locale }),
      },
      {
        name: "preferredContractType",
        label: "app.report.preferredContractType",
        enable: true,
        definition: () =>
          newColumn("app.report.preferredContractType", "preferredContractType", "string", "center", (rowData) => intl.formatMessage({ id: `app.enuns.${rowData.preferredContractType}` })),
        exportValue: (rowData) => intl.formatMessage({ id: `app.enuns.${rowData.preferredContractType}` }),
      },
      {
        name: "custodialValue",
        label: "app.custodian.total",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.custodian.total", "custodialValue", "", currentUser.hideValues),
        exportValue: (rowData) => formatCurrency({ value: rowData.custodialValue, userLocale: currentUser.locale }),
      },
      {
        name: "custodialPercent",
        label: "app.percentage.custodialValue",
        enable: true,
        definition: () => newColumnPercentage("app.percentage.custodialValue", "custodialPercent", true),
        exportValue: (rowData) => formatPercentage({ value: rowData.custodialPercent, userLocale: currentUser.locale }),
      },
      {
        name: "definition",
        label: "app.report.averageInvested",
        enable: true,
        definition: () => newColumnValueWithNumberComponent("app.report.averageInvested", "averageInvestment", "", currentUser.hideValues),
        exportValue: (rowData) => formatCurrency({ value: rowData.averageInvestment, userLocale: currentUser.locale }),
      },
      {
        name: "averageAge",
        label: "app.report.averageAge",
        enable: true,
        definition: () => newColumn("app.report.averageAge", "averageAge", "string", "center"),
      },
      {
        name: "averageDuration",
        label: "app.report.averageDuration",
        enable: true,
        definition: () => newColumn("app.report.averageDuration", "averageDuration", "string", "center"),
      },
    ];
  }, [currentUser.hideValues, currentUser.locale, intl, userFilters.group]);

  return (
    <PageContainer full>
      <HeaderReportUsersGroup
        title={pageTitle}
        userFilters={userFilters}
      />

      <AnimatedCardContent>
        <UserFilters
          userFilters={userFilters}
          setUserFilters={setUserFilters}
          WrapperComponent={WrapperComponent}
          fetchCitiesEnabled={fetchCitiesEnabled}
          fetchStatesEnabled={fetchStatesEnabled}
        />
      </AnimatedCardContent>

      {!userFilters.group ? (
        <AnimatedCardContent raised={false}>
          <Typography
            color="primary"
            variant="h6"
            align="center"
          >
            <FormattedMessage id="app.report.group.select" />
          </Typography>
        </AnimatedCardContent>
      ) : (
        <>
          <UsersReportSummary
            currentUser={currentUser}
            summary={reportData}
            disabled={loading || error}
          />

          <AnimatedCardContent>
            <ReportTable
              availableColumns={availableColumns}
              currentUser={currentUser}
              title={`app.report.title.${userFilters.group}`}
              data={reportData?.reportsByGroup}
              loading={loading}
              error={error}
              enableCopy
              reportKey={"reportByGroup"}
            />
          </AnimatedCardContent>
        </>
      )}
    </PageContainer>
  );
};

const Wrapper = React.memo(({ children }) => (
  <Grid
    item
    xs={12}
    sm={6}
    md={3}
  >
    {children}
  </Grid>
));

export default injectIntl(ReportByGroupContext);
